import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Typography } from "@mui/material"
import { useMutation } from "@tanstack/react-query"
import { CreateContactUsPublicRequest, postContactsUsCreate } from "api"
import "dayjs/locale/he"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { Dispatch, LegacyRef, SetStateAction, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import { MarketingConsent } from "ui"
import {
  applicationRequestError,
  applicationRequestSuccess,
  useFocusOnError,
} from "utils"
import { commonSchema, messagesInvalid, regex } from "utils/FormSchema"
import * as yup from "yup"
import { FormId } from "../MainForm"
import {
  CustomTextField,
  Header,
  isFieldRequired,
  MultilineCustomTextField,
  NumbersController,
  prefixOptionsArray,
  RECAPTCHA_KEY,
} from "./Common"
import { FormContainer, InputsGrid, StyledButton } from "./styles"

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 48px;
  }
`

const StyledTextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 700px;
`

const StyledUl = styled.ul`
  margin: 0;
`

const FileUploadContainer = styled.div<{ error: boolean }>`
  display: flex;
  height: 56px;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 12px;
  border: 1px dashed ${(props) => (props.error ? "#E00700" : "#d1d5db")};
  background: var(--White, #fff);
  position: relative;
`

const FileInput = styled.input`
  width: 100%;
  height: 100%;
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
  cursor: pointer;
`

const FileInputWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const FileLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: space-between;
  position: absolute;
  padding: 12px;
  right: 0;
  width: 100%;
  color: #6f747d;
`

const FileName = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6f747d;
  max-width: 80%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`

export default function RequestForTaxInvoice({
  handleResult,
  locale,
  contactType,
  setIsLoading,
  label,
  tooltipText,
}: {
  handleResult: (result: "yes" | "no", id?: string) => void
  locale: "he" | "en" | "ar" | "ru"
  contactType: FormId
  setIsLoading: Dispatch<SetStateAction<boolean>>
  label: string
  tooltipText: string
}) {
  const [isPending, setIsPending] = useState(false)
  const t = useTranslations("Forms")
  const tInner = useTranslations("InnerPages")
  const reCaptchaRef = useRef<ReCAPTCHA>(null)
  const schema = yup.object({
    firstName: commonSchema.common(locale).firstName,
    lastName: commonSchema.common(locale).lastName,
    customerId: yup
      .string()
      .matches(regex.onlyNumbers, messagesInvalid[locale].customerId)
      .max(25, messagesInvalid[locale].customerId)
      .min(2, messagesInvalid[locale].fullNameTwo)
      .optional(),
    ravKavAccount: commonSchema.common(locale).ravkav,
    email: commonSchema.common(locale).email,
    cellphone: commonSchema.common(locale).phonePrefix,
    phone: commonSchema.common(locale).phone,
    content: commonSchema.common(locale).inquiryDescription,
    mailingConsent: yup.boolean().optional(),
    file: yup
      .mixed<File>()
      .required("File is required")
      .test("fileSize", "File size is too large", (value) => {
        if (!value) return true
        return (value as File).size <= 2 * 1024 * 1024 * 1024 // 2GB
      })
      .test("fileType", "Only PDF files are allowed", (value) => {
        if (!value) return true
        return (value as File).type === "application/pdf"
      }),
  })

  type FormData = yup.InferType<typeof schema>

  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitted, isValid },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      cellphone: prefixOptionsArray[0].value,
    },
  })

  // Add focus management for validation errors
  useFocusOnError(errors, isSubmitted)

  const { mutate } = useMutation({
    mutationFn: postContactsUsCreate,
    onMutate: () => {
      setIsLoading(true)
    },
    onSuccess: (data) => {
      if (data.data.errorCode) {
        applicationRequestError(tInner("submitInquiry"), label)
        handleResult("yes")
      } else {
        applicationRequestSuccess(tInner("submitInquiry"), label)
        handleResult("no", data.data.body.caseNumber)
      }
    },
    onError: () => {
      applicationRequestError(tInner("submitInquiry"), label)
      handleResult("yes")
    },
    onSettled: () => {
      setIsLoading(false)
      setIsPending(false)
    },
  })

  const onSubmit = async (data: FormData) => {
    setIsPending(true)
    reCaptchaRef?.current?.reset()
    const recaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string

    let attachment = null
    if (data.file) {
      const base64Content = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.readAsDataURL(data.file as File)
        reader.onload = () => resolve(reader.result)
      })

      attachment = {
        fileName: (data.file as File).name,
        base64Content: base64Content as string,
      }
    }

    const { file, ...dataWithoutFile } = data
    const formattedData = {
      ...dataWithoutFile,
      contactTypeId: contactType,
      recaptchaResponse,
      attachment,
      sid: window?.__ctm?.config?.sid || "-1",
    }
    mutate(formattedData as unknown as CreateContactUsPublicRequest)
  }

  return (
    <Layout>
      {locale === "he" ? (
        <StyledTextBox>
          <Typography variant="body3">
            טופס זה מיועד להעברת בקשה לזיכוי על חיוב שבוצע בכרטיס אשראי בקווי
            הזמנת מקום.
            <br />
            בסוף לא עלית על האוטובוס? - אנחנו נבדוק ונבטל את חיוב האשראי שלך.
            <br />
            לבירורים ופרטים נוספים ניתן לפנות למוקד השרות בטלפון *2800
          </Typography>
          <Typography variant="body3">
            נוסע המבקש לקבל חשבונית מס עבור כרטיסי נסיעה שנרכשו באגד או הטענת
            חוזי נסיעה שבוצעה באגד - יש לרשום בפנייה את מספר רב קו ומספר תעודת
            הזהות של בעל הרב קו שבו בוצעו הנסיעות. מובהר כי חשבוניות יינתנו על
            רכישות והטענות שבוצעו באחת הדרכים הבאות בלבד:
          </Typography>
          <StyledUl>
            <li>
              <Typography variant="body3">בקופות הכרטיסים של אגד</Typography>
            </li>
            <li>
              <Typography variant="body3">בעמדות ״על הקו״ של אגד</Typography>
            </li>
            <li>
              <Typography variant="body3">
                במכשירי טעינה אוטומטיים של אגד
              </Typography>
            </li>
          </StyledUl>
          <Typography variant="body3">
            בגין רכישות שבוצעו אצל נציגי מכירות כגון רב קו און-ליין, הופ-און, או
            כל שירות אחר המאפשר טעינה, בין אם באינטרנט, באפליקציה או במכשירים
            אוטומטיים של אותן חברות, יש להפנות את הבקשה אל החברה בה בוצעה
            הטעינה. חברת אגד לא תטפל בפניות אלו
          </Typography>
        </StyledTextBox>
      ) : (
        <StyledTextBox>
          <Typography variant="body3">
            This form is intended for submitting a credit request for a charge
            made to a credit card through the seat reservation system.
            <br />
            Didn't board the bus after booking a seat? We will review your case
            and cancel the credit card charge if applicable. <br />
            For further information, please contact our customer service center
            at *2800.
          </Typography>
          <Typography variant="body3">
            Passengers wishing to receive a tax invoice for bus tickets
            purchased through Egged or for travel card (Rav-Kav) top-ups made
            via Egged must include the Rav-Kav card number and the ID number of
            the cardholder used for the travel. <br />
            Please note that invoices will be issued only for purchases or
            top-ups made through one of the following methods:
          </Typography>
          <StyledUl>
            <li>
              <Typography variant="body3">Egged ticket offices</Typography>
            </li>
            <li>
              <Typography variant="body3">
                Egged "Al HaKav" service stations
              </Typography>
            </li>
            <li>
              <Typography variant="body3">
                Egged automated top-up machines
              </Typography>
            </li>
          </StyledUl>
          <Typography variant="body3">
            For purchases made via third-party sales representatives such as
            'Rav-Kav Online', 'HopOn', or any other service that enables
            top-ups—whether via website, mobile app, or automated machines
            operated by these companies—requests for tax invoices must be
            directed to the company through which the transaction was made.
            Egged will not process these requests.
          </Typography>
        </StyledTextBox>
      )}
      <FormContainer onSubmit={handleSubmit(onSubmit)}>
        <Header title={tInner("fillDetails")} />
        <InputsGrid>
          <CustomTextField
            name="firstName"
            control={control}
            label={t("firstName")}
            required={isFieldRequired(schema, "firstName")}
            isSubmitted={isSubmitted}
            errors={errors}
          />
          <CustomTextField
            name="lastName"
            control={control}
            label={t("lastName")}
            required={isFieldRequired(schema, "lastName")}
            isSubmitted={isSubmitted}
            errors={errors}
          />
          <CustomTextField
            name="email"
            control={control}
            label={t("email")}
            required={isFieldRequired(schema, "email")}
            isSubmitted={isSubmitted}
            errors={errors}
          />
          <NumbersController
            prefixName="cellphone"
            phoneName="phone"
            control={control}
            label={t("mobileNumber")}
            required={isFieldRequired(schema, "phone")}
            isSubmitted={isSubmitted}
            errors={errors}
            locale={locale}
          />
        </InputsGrid>
        <InputsGrid>
          <CustomTextField
            name="customerId"
            control={control}
            label={t("idCard")}
            required={isFieldRequired(schema, "customerId")}
            isSubmitted={isSubmitted}
            errors={errors}
          />
          <CustomTextField
            name="ravKavAccount"
            control={control}
            label={t("ravKav")}
            required={isFieldRequired(schema, "ravKavAccount")}
            isSubmitted={isSubmitted}
            errors={errors}
          />
        </InputsGrid>

        <InputsGrid>
          <div style={{ width: "100%" }}>
            <FileUploadContainer error={!!errors.file}>
              {!watch("file") ? (
                <>
                  <FileLabel>
                    <Typography
                      variant="body3"
                      component="label"
                      htmlFor="file"
                      sx={{ cursor: "pointer" }}
                    >
                      {locale === "he" ? "הוספת קובץ" : "Add a file"}
                      <span style={{ color: "#E00700" }}>*</span>
                    </Typography>
                    <Image
                      src={"/icons/paperclip.svg"}
                      alt="paperclip"
                      width={20}
                      height={20}
                    />
                  </FileLabel>
                </>
              ) : (
                <>
                  <FileLabel>
                    <FileName>{(watch("file") as File)?.name}</FileName>
                    <IconButton
                      style={{ zIndex: 2 }}
                      onClick={(e) => {
                        e.preventDefault()
                        setValue("file", undefined as any)
                      }}
                      aria-label={locale === "he" ? "הסר קובץ" : "Remove file"}
                    >
                      <Image
                        src={"/icons/close.svg"}
                        alt="close"
                        width={20}
                        height={20}
                      />
                    </IconButton>
                  </FileLabel>
                </>
              )}
              <FileInputWrapper>
                <FileInput
                  type="file"
                  accept=".pdf"
                  id="file"
                  required={true}
                  aria-label={locale === "he" ? "הוספת קובץ" : "Add a file"}
                  aria-required="true"
                  onChange={(e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      setValue("file", file, { shouldValidate: true })
                    }
                  }}
                  onClick={(e) => {
                    if (isSubmitted) {
                      e.currentTarget.value = ""
                    }
                  }}
                />
              </FileInputWrapper>
            </FileUploadContainer>
            <Typography
              variant="body3"
              sx={{ color: "var(--Grey-Gray-M, #D1D5DB)" }}
            >
              ניתן לעלות קובץ עד משקל GB 2
            </Typography>
          </div>
        </InputsGrid>

        <MultilineCustomTextField
          name="content"
          control={control}
          label={t("inquiryDescription")}
          required={isFieldRequired(schema, "content")}
          isSubmitted={isSubmitted}
          errors={errors}
        />
        <MarketingConsent
          name="mailingConsent"
          control={control}
          tooltipText={tooltipText}
        />
        <StyledButton variant="primary" type="submit" disabled={isPending}>
          {tInner("submitInquiry")}
        </StyledButton>
        {RECAPTCHA_KEY && (
          <ReCAPTCHA
            ref={reCaptchaRef as LegacyRef<ReCAPTCHA>}
            sitekey={RECAPTCHA_KEY}
            size="invisible"
            hl="iw"
            tabIndex={-1}
          />
        )}
      </FormContainer>
    </Layout>
  )
}
